import React, { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { Pencil, Loader2, AlertCircle, Plus, Trash2, Search } from "lucide-react";
import { Card, CardContent } from "@pgs-core/ui/components/card";
import { Badge } from "@pgs-core/ui/components/badge";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import { Label } from "@pgs-core/ui/components/label";
import { Alert, AlertTitle, AlertDescription } from "@pgs-core/ui/components/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@pgs-core/ui/components/dialog";
import { ConfirmDeleteDialog } from "@pgs-core/ui/components/confirm-dialog";
import { LoadingSpinner } from "@pgs-core/ui/components/loading-spinner";
import { PaginationControls } from "@pgs-core/ui/components/pagination-controls";
import { formatTimestamp, parseTimestamp } from "@pgs-core/portal-lib/format";
import { useApiQuery } from "@pgs-core/portal-lib/hooks";
import { useListControls } from "@pgs-core/portal-lib/list-controls";
import { PageHeader } from "@pgs-core/ui/components/page-header";
import type { ClientOutletContext } from "../lib/outlet-context";

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

interface PageRow {
  slug: string;
  title: string;
  published?: number;
  updated_at?: string | null;
}

interface PageBadge {
  label: string;
  variant: "secondary" | "warning" | "success";
}

// The per-page badge reflects the *live* state, not just the visibility toggle.
// A published page edited since the last build is only queued — it goes live on the
// next Publish. Mirrors the server's hasUnpublishedChanges convention (tenant.js).
function pageStatus(page: PageRow, buildDispatchedAt: string | null | undefined): PageBadge {
  if (!page.published) return { label: "Draft", variant: "secondary" };
  const dispatched = parseTimestamp(buildDispatchedAt);
  const updated = parseTimestamp(page.updated_at);
  const pending = !dispatched || (updated && updated > dispatched);
  return pending
    ? { label: "Queued", variant: "warning" }
    : { label: "Published", variant: "success" };
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);
}

function slugProblem(slug: string): string {
  if (!SLUG_PATTERN.test(slug) || slug.length > 50) {
    return "Use lowercase letters, numbers and hyphens (start with a letter or number).";
  }
  if (slug === "home") return '"home" is reserved.';
  return "";
}

export function PagesPage() {
  const { api, status, refreshStatus, activeModules } = useOutletContext<ClientOutletContext>();
  const navigate = useNavigate();
  // The menu module owns the /menu page: it appears in the list so clients can
  // see it exists on their site, but its content is edited under Menu, not the
  // page CMS — so the row's edit/delete are disabled.
  const menuModuleActive = Array.isArray(activeModules) && activeModules.includes("restaurant_menu");
  const controls = useListControls();
  const query = useApiQuery(
    () => (api ? api.request<{ pages?: PageRow[]; total?: number }>(`/tenant/pages?${controls.queryString}`) : null),
    [api, controls.queryString]
  );
  const pages = query.data?.pages ?? null;
  const total = query.data?.total ?? 0;
  const [error, setError] = useState("");

  // The home page (slug "home", served at "/") always sits at the top of the
  // list; everything else keeps the order the API returned it in.
  const orderedPages = pages
    ? [...pages].sort((a, b) => (a.slug === "home" ? -1 : b.slug === "home" ? 1 : 0))
    : null;

  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<PageRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const openCreate = () => {
    setNewTitle("");
    setNewSlug("");
    setSlugTouched(false);
    setCreateError("");
    setCreateOpen(true);
  };

  const onTitleChange = (value: string) => {
    setNewTitle(value);
    if (!slugTouched) setNewSlug(slugify(value));
  };

  const createSlugError = newSlug ? slugProblem(newSlug) : "";

  const createPage = async () => {
    if (!api) return;
    if (!newTitle.trim()) {
      setCreateError("Enter a page title.");
      return;
    }
    if (createSlugError) {
      setCreateError(createSlugError);
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      const result = await api.request<{ slug: string }>("/tenant/pages", {
        method: "POST",
        body: JSON.stringify({ title: newTitle.trim(), slug: newSlug.trim() }),
      });
      setCreateOpen(false);
      refreshStatus?.();
      navigate(`/pages/${result.slug}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  };

  const deletePage = async () => {
    if (!api || !deleteTarget) return;
    setDeleting(true);
    setError("");
    try {
      await api.request(`/tenant/pages/${deleteTarget.slug}`, { method: "DELETE" });
      query.reload();
      setDeleteTarget(null);
      refreshStatus?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Pages"
        description="Create pages, save your edits, then publish the site when you're ready."
        actions={
          <Button onClick={openCreate} size="sm">
            <Plus className="h-4 w-4" />
            Create page
          </Button>
        }
      />

      {(error || query.error) && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error || query.error}</AlertDescription>
        </Alert>
      )}

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={controls.search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => controls.setSearch(e.target.value)}
          placeholder="Search pages…"
          className="pl-8"
          aria-label="Search pages"
        />
      </div>

      {!pages && !query.error && <LoadingSpinner className="py-20" />}

      {orderedPages && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Title</th>
                  <th className="px-4 py-3 font-medium">Slug</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Last updated</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {orderedPages.length === 0 && !menuModuleActive && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                      {controls.q
                        ? "No pages match your search."
                        : "No pages yet — create your first page to get started."}
                    </td>
                  </tr>
                )}
                {orderedPages.map((page) => {
                  const pageState = pageStatus(page, status?.build_dispatched_at);
                  return (
                  <tr key={page.slug} className="border-b last:border-0 hover:bg-muted/50">
                    <td className="px-4 py-3 font-medium">{page.title}</td>
                    <td className="px-4 py-3 text-muted-foreground">/{page.slug}</td>
                    <td className="px-4 py-3">
                      <Badge variant={pageState.variant}>
                        {pageState.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{formatTimestamp(page.updated_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button asChild variant="outline" size="sm">
                          <Link to={`/pages/${page.slug}`}>
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-destructive"
                          disabled={page.slug === "home"}
                          title={page.slug === "home" ? "The home page can't be deleted" : "Delete page"}
                          onClick={() => setDeleteTarget(page)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {menuModuleActive && (
                  <tr className="border-b last:border-0 bg-muted/30">
                    <td className="px-4 py-3 font-medium">Menu</td>
                    <td className="px-4 py-3 text-muted-foreground">/menu</td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary" title="Content comes from the Menu module">
                        Menu module
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">—</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled
                          title="This page is managed by the Menu module — edit it under Menu"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground"
                          disabled
                          title="Managed by the Menu module — deactivate the module to remove it"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <PaginationControls
              page={controls.page}
              total={total}
              perPage={controls.perPage}
              onPageChange={controls.setPage}
              onPerPageChange={controls.setPerPage}
              itemsLabel="pages"
              className="border-t px-4 py-3"
            />
          </CardContent>
        </Card>
      )}

      {/* Create page */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a page</DialogTitle>
            <DialogDescription>
              New pages start as a draft. Save your content, then publish the site to make it live.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-title">Title</Label>
              <Input
                id="new-title"
                value={newTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onTitleChange(e.target.value)}
                placeholder="e.g. Our Team"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-slug">URL slug</Label>
              <Input
                id="new-slug"
                value={newSlug}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setNewSlug(e.target.value); setSlugTouched(true); }}
                placeholder="our-team"
                aria-invalid={!!createSlugError}
              />
              {createSlugError ? (
                <p className="text-xs text-destructive">{createSlugError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">Lives at /{newSlug || "your-page"}/</p>
              )}
            </div>
            {createError && <p className="text-sm text-destructive">{createError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={createPage} disabled={creating || !newTitle.trim() || !!createSlugError}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete "${deleteTarget?.title}"?`}
        description="This removes the page from your portal. It stays on your live site until you next publish. This can't be undone."
        confirmLabel="Delete page"
        busy={deleting}
        onConfirm={deletePage}
      />
    </div>
  );
}
